/**
 * Relationship count helpers for list views.
 *
 * Loads all edges of a given predicate in one indexed query, then groups
 * them in memory by either subject or object entity id, filtered to a
 * specific node type. Used by plan-list, activity-list, and routes-list
 * to show related-entity counts on each card without N+1 queries.
 */

import { edgeService, parseNodeRef } from '../services/edge-service.js'
import type { Predicate } from '../schemas/edge.js'

/**
 * Count edges grouped by the subject side.
 *
 * Example: `countsBySubject('follows-route', 'activity')` returns a
 * `Map<activityId, number>` where the number is how many routes that activity follows.
 */
export async function countsBySubject(
  predicate: Predicate,
  subjectType: string
): Promise<Map<string, number>> {
  const edges = await edgeService.getByPredicate(predicate)
  const counts = new Map<string, number>()
  for (const edge of edges) {
    const { type, id } = parseNodeRef(edge.subject)
    if (type !== subjectType) continue
    counts.set(id, (counts.get(id) ?? 0) + 1)
  }
  return counts
}

/**
 * Count edges grouped by the object side.
 *
 * Example: `countsByObject('follows-route', 'route')` returns a
 * `Map<routeId, number>` where the number is how many activities follow
 * that route.
 */
export async function countsByObject(
  predicate: Predicate,
  objectType: string
): Promise<Map<string, number>> {
  const edges = await edgeService.getByPredicate(predicate)
  const counts = new Map<string, number>()
  for (const edge of edges) {
    const { type, id } = parseNodeRef(edge.object)
    if (type !== objectType) continue
    counts.set(id, (counts.get(id) ?? 0) + 1)
  }
  return counts
}
